import { useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "./PegmanControl.scss";

const PEGMAN_ICON_PATH = "/images/icons8-street-view-96.png";

const CLOSE_ICON_SVG =
  '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
  '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';

const COVERAGE_LAYER_OPACITY_HIDDEN = 0;
const COVERAGE_LAYER_OPACITY_VISIBLE = 1;
const COVERAGE_TILE_URL =
  "https://{s}.google.com/vt?lyrs=svv|cb_client:apiv3&style=40,18&x={x}&y={y}&z={z}";
const GOOGLE_MAPS_API_SELECTOR =
  'script[data-pegman-google-maps-api="true"], script[src*="maps.googleapis.com/maps/api/js"]';

function getGoogleMaps() {
  return globalThis.google?.maps;
}

function getFullscreenElement() {
  return (
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement
  );
}

function waitForGoogleMapsApi(timeoutMs = 10000) {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    const intervalId = globalThis.setInterval(() => {
      const googleMaps = getGoogleMaps();

      if (googleMaps?.StreetViewService) {
        globalThis.clearInterval(intervalId);
        resolve(googleMaps);
        return;
      }

      if (Date.now() - startTime >= timeoutMs) {
        globalThis.clearInterval(intervalId);
        reject(new Error("Google Maps failed to become ready"));
      }
    }, 100);
  });
}

function loadGoogleMapsApi(apiKey) {
  const googleMaps = getGoogleMaps();

  if (googleMaps?.StreetViewService) {
    return Promise.resolve(googleMaps);
  }

  const existingScript = document.querySelector(GOOGLE_MAPS_API_SELECTOR);
  if (existingScript) {
    return waitForGoogleMapsApi();
  }

  return new Promise((resolve, reject) => {
    const callbackName = "__svPegmanReady_" + Date.now();
    const script = document.createElement("script");

    globalThis[callbackName] = function () {
      delete globalThis[callbackName];
      waitForGoogleMapsApi(5000).then(resolve).catch(reject);
    };

    script.dataset.pegmanGoogleMapsApi = "true";
    script.src =
      "https://maps.googleapis.com/maps/api/js?key=" +
      encodeURIComponent(apiKey) +
      "&callback=" +
      callbackName;
    script.async = true;
    script.onerror = function () {
      delete globalThis[callbackName];
      reject(new Error("Google Maps script failed to load"));
    };

    document.head.appendChild(script);
  });
}

function checkStreetViewCoverage(latlng) {
  return new Promise((resolve) => {
    const googleMaps = getGoogleMaps();

    if (!googleMaps?.StreetViewService) {
      resolve(null);
      return;
    }

    const svService = new googleMaps.StreetViewService();
    svService
      .getPanorama({
        location: { lat: latlng.lat, lng: latlng.lng },
        radius: 50,
        source: googleMaps.StreetViewSource.OUTDOOR,
      })
      .then(function (result) {
        resolve(result.data);
      })
      .catch(function () {
        resolve(null);
      });
  });
}

const PegmanControl = ({ apiKey }) => {
  const map = useMap();
  const stateRef = useRef("idle");
  const controlRef = useRef(null);
  const buttonElRef = useRef(null);
  const ghostRef = useRef(null);
  const overlayRef = useRef(null);
  const closeRef = useRef(null);
  const panoramaRef = useRef(null);
  const markerRef = useRef(null);
  const coverageLayerRef = useRef(null);
  const toastTimerRef = useRef(null);
  const toastElRef = useRef(null);
  const apiLoadedRef = useRef(false);
  const apiLoadPromiseRef = useRef(null);
  const mapClickHandlerRef = useRef(null);

  const getDragGhostHost = useCallback(() => {
    const fullscreenElement = getFullscreenElement();
    const mapContainer = map.getContainer();

    if (fullscreenElement?.contains(mapContainer)) {
      return mapContainer;
    }

    return document.body;
  }, [map]);

  const hidePegmanControl = useCallback(() => {
    const buttonWrapper = buttonElRef.current?.parentNode;

    if (buttonWrapper) {
      buttonWrapper.style.display = "none";
    }
  }, []);

  const ensureApi = useCallback(() => {
    if (apiLoadedRef.current) return Promise.resolve();
    if (apiLoadPromiseRef.current) return apiLoadPromiseRef.current;
    apiLoadPromiseRef.current = loadGoogleMapsApi(apiKey || "")
      .then(() => {
        apiLoadedRef.current = true;
      })
      .catch((error) => {
        apiLoadPromiseRef.current = null;
        throw error;
      });
    return apiLoadPromiseRef.current;
  }, [apiKey]);

  const ensureCoverageLayer = useCallback(() => {
    if (!coverageLayerRef.current) {
      coverageLayerRef.current = L.tileLayer(COVERAGE_TILE_URL, {
        subdomains: ["mt0", "mt1", "mt2", "mt3"],
        opacity: COVERAGE_LAYER_OPACITY_HIDDEN,
        maxZoom: 20,
      });
    }

    if (!map.hasLayer(coverageLayerRef.current)) {
      coverageLayerRef.current.addTo(map);
    }

    return coverageLayerRef.current;
  }, [map]);

  const showCoverageLayer = useCallback(() => {
    ensureCoverageLayer().setOpacity(COVERAGE_LAYER_OPACITY_VISIBLE);
  }, [ensureCoverageLayer]);

  const hideCoverageLayer = useCallback(() => {
    if (coverageLayerRef.current) {
      coverageLayerRef.current.setOpacity(COVERAGE_LAYER_OPACITY_HIDDEN);
    }
  }, []);

  const showNoCoverageToast = useCallback(() => {
    if (toastElRef.current?.parentNode) {
      toastElRef.current.remove();
    }
    const container = map.getContainer();
    const toast = document.createElement("div");
    toast.className = "pegman-no-coverage-toast";
    toast.textContent = "Geen Street View beschikbaar";
    container.appendChild(toast);
    toastElRef.current = toast;
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(function () {
      if (toast.parentNode) toast.remove();
      if (toastElRef.current === toast) toastElRef.current = null;
    }, 2500);
  }, [map]);

  const placePegmanMarker = useCallback(
    (latlng) => {
      if (markerRef.current) {
        markerRef.current.setLatLng(latlng);
        return;
      }
      const icon = L.icon({
        className: "pegman-marker-icon",
        iconUrl: PEGMAN_ICON_PATH,
        iconSize: [40, 40],
        iconAnchor: [20, 36],
      });
      markerRef.current = L.marker(latlng, { icon: icon, interactive: false });
      markerRef.current.addTo(map);
    },
    [map]
  );

  const removePegmanMarker = useCallback(() => {
    if (markerRef.current) {
      map.removeLayer(markerRef.current);
      markerRef.current = null;
    }
  }, [map]);

  const updateMarkerHeading = useCallback((heading) => {
    if (!markerRef.current) return;
    const el = markerRef.current.getElement();
    if (el) {
      el.style.transformOrigin = "center bottom";
      const currentLabel = el.style.transform || "";
      const translatePart = currentLabel.match(/translate3d\([^)]+\)/) ? currentLabel.match(/translate3d\([^)]+\)/)[0] : "";
      el.style.transform = `${translatePart} rotate(${heading}deg)`;
    }
  }, []);

  const openPanorama = useCallback(
    (latlng) => {
      const googleMaps = getGoogleMaps();

      if (!googleMaps?.StreetViewPanorama) return;

      stateRef.current = "panorama";
      hideCoverageLayer();

      const container = map.getContainer();
      container.classList.remove("pegman-pick-cursor");
      if (buttonElRef.current) {
        buttonElRef.current.classList.remove(
          "leaflet-control-pegman-button--active"
        );
      }

      placePegmanMarker(latlng);

      if (!overlayRef.current) return;
      overlayRef.current.classList.add("pegman-panorama-overlay--visible");

      if (panoramaRef.current) {
        panoramaRef.current.setPosition({
          lat: latlng.lat,
          lng: latlng.lng,
        });
      } else {
        panoramaRef.current = new googleMaps.StreetViewPanorama(
          overlayRef.current,
          {
            position: { lat: latlng.lat, lng: latlng.lng },
            pov: { heading: 0, pitch: 0 },
            zoom: 1,
            addressControl: true,
            fullscreenControl: false,
            enableCloseButton: false,
            linksControl: true,
          }
        );

        googleMaps.event.addListener(
          panoramaRef.current,
          "position_changed",
          function () {
            const pos = panoramaRef.current.getPosition();
            if (!pos) return;
            const newLatLng = L.latLng(pos.lat(), pos.lng());
            placePegmanMarker(newLatLng);
            if (!map.getBounds().contains(newLatLng)) {
              map.panTo(newLatLng);
            }
          }
        );

        googleMaps.event.addListener(
          panoramaRef.current,
          "pov_changed",
          function () {
            const pov = panoramaRef.current.getPov();
            if (pov) updateMarkerHeading(pov.heading);
          }
        );
      }
    },
    [hideCoverageLayer, map, placePegmanMarker, updateMarkerHeading]
  );

  const closePanorama = useCallback(() => {
    const googleMaps = getGoogleMaps();

    stateRef.current = "idle";

    if (overlayRef.current) {
      overlayRef.current.classList.remove("pegman-panorama-overlay--visible");
    }

    if (panoramaRef.current && googleMaps?.event) {
      googleMaps.event.clearInstanceListeners(panoramaRef.current);
      panoramaRef.current = null;
    }

    removePegmanMarker();
  }, [removePegmanMarker]);

  useEffect(() => {
    const isTouchDevice = "ontouchstart" in globalThis;
    const container = map.getContainer();

    const overlayDiv = document.createElement("div");
    overlayDiv.className = "pegman-panorama-overlay";
    container.appendChild(overlayDiv);
    overlayRef.current = overlayDiv;

    const closeBtn = document.createElement("button");
    closeBtn.className = "pegman-panorama-close";
    closeBtn.innerHTML = CLOSE_ICON_SVG;
    closeBtn.title = "Street View sluiten";
    closeBtn.type = "button";
    overlayDiv.appendChild(closeBtn);
    closeRef.current = closeBtn;

    function onCloseClick(e) {
      e.stopPropagation();
      closePanorama();
    }
    closeBtn.addEventListener("click", onCloseClick);

    const control = L.control({ position: "topleft" });
    let wrapperEl = null;

    const onPointerMove = (e) => {
      if (stateRef.current !== "dragging") return;

      if (ghostRef.current) {
        ghostRef.current.style.left = e.clientX + "px";
        ghostRef.current.style.top = e.clientY + "px";
      }
    };

    const onPointerCancel = () => {
      if (stateRef.current !== "dragging") return;

      if (ghostRef.current) {
        ghostRef.current.remove();
        ghostRef.current = null;
      }

      map.dragging.enable();
      hideCoverageLayer();
      stateRef.current = "idle";
    };

    const onMobileMapClick = async (e) => {
      if (stateRef.current !== "picking") return;

      if (!apiLoadedRef.current) {
        try {
          await ensureApi();
        } catch {
          stateRef.current = "idle";
          hideCoverageLayer();
          hidePegmanControl();
          return;
        }
      }

      if (stateRef.current !== "picking") return;

      const data = await checkStreetViewCoverage(e.latlng);

      if (stateRef.current !== "picking") return;

      if (data) {
        const loc = data.location.latLng;

        if (mapClickHandlerRef.current) {
          map.off("click", mapClickHandlerRef.current);
          mapClickHandlerRef.current = null;
        }

        openPanorama(L.latLng(loc.lat(), loc.lng()));
        return;
      }

      showNoCoverageToast();
    };

    const onClickPrevent = (e) => {
      L.DomEvent.stop(e);
    };

    control.onAdd = function () {
      wrapperEl = L.DomUtil.create(
        "div",
        "leaflet-bar leaflet-control leaflet-control-pegman"
      );
      const btn = L.DomUtil.create(
        "a",
        "leaflet-control-pegman-button",
        wrapperEl
      );
      btn.href = "#";
      btn.role = "button";
      btn.title = "Street View";
      const img = document.createElement("img");
      img.src = PEGMAN_ICON_PATH;
      img.alt = "Street View";
      btn.appendChild(img);
      buttonElRef.current = btn;

      L.DomEvent.disableClickPropagation(wrapperEl);

      if (isTouchDevice) {
        setupMobileTap(btn);
      } else {
        setupDesktopDrag(btn);
      }

      return wrapperEl;
    };

    function setupDesktopDrag(btn) {
      function onPointerDown(e) {
        if (stateRef.current === "panorama") {
          closePanorama();
          return;
        }
        if (stateRef.current !== "idle") return;

        e.preventDefault();
        stateRef.current = "dragging";
        btn.setPointerCapture(e.pointerId);
        map.dragging.disable();

        const ghost = document.createElement("img");
        ghost.className = "pegman-drag-ghost";
        ghost.src = PEGMAN_ICON_PATH;
        ghost.style.left = e.clientX + "px";
        ghost.style.top = e.clientY + "px";
        getDragGhostHost().appendChild(ghost);
        ghostRef.current = ghost;

        showCoverageLayer();
      }

      async function onPointerUp(e) {
        if (stateRef.current !== "dragging") return;

        if (ghostRef.current) {
          ghostRef.current.remove();
          ghostRef.current = null;
        }
        map.dragging.enable();

        const rect = container.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
          stateRef.current = "idle";
          hideCoverageLayer();
          return;
        }

        const latlng = map.containerPointToLatLng(L.point(x, y));

        if (!apiLoadedRef.current) {
          try {
            await ensureApi();
          } catch {
            stateRef.current = "idle";
            hideCoverageLayer();
            hidePegmanControl();
            return;
          }
        }

        if (stateRef.current !== "dragging") {
          hideCoverageLayer();
          return;
        }

        const data = await checkStreetViewCoverage(latlng);

        if (stateRef.current !== "dragging") {
          hideCoverageLayer();
          return;
        }

        if (data) {
          const loc = data.location.latLng;
          openPanorama(L.latLng(loc.lat(), loc.lng()));
        } else {
          showNoCoverageToast();
          stateRef.current = "idle";
        }

        hideCoverageLayer();
      }

      function onPointerEnter() {
        ensureApi().catch(() => {});
      }

      btn.addEventListener("pointerenter", onPointerEnter);
      btn.addEventListener("pointerdown", onPointerDown);
      btn.addEventListener("pointermove", onPointerMove);
      btn.addEventListener("pointerup", onPointerUp);
      btn.addEventListener("pointercancel", onPointerCancel);
      L.DomEvent.on(btn, "click", onClickPrevent);

      btn._pegmanCleanup = function () {
        btn.removeEventListener("pointerenter", onPointerEnter);
        btn.removeEventListener("pointerdown", onPointerDown);
        btn.removeEventListener("pointermove", onPointerMove);
        btn.removeEventListener("pointerup", onPointerUp);
        btn.removeEventListener("pointercancel", onPointerCancel);
        L.DomEvent.off(btn, "click", onClickPrevent);
      };
    }

    function setupMobileTap(btn) {
      function onBtnClick(e) {
        L.DomEvent.stop(e);

        if (stateRef.current === "panorama") {
          closePanorama();
          return;
        }

        if (stateRef.current === "picking") {
          stateRef.current = "idle";
          btn.classList.remove("leaflet-control-pegman-button--active");
          container.classList.remove("pegman-pick-cursor");
          hideCoverageLayer();
          if (mapClickHandlerRef.current) {
            map.off("click", mapClickHandlerRef.current);
            mapClickHandlerRef.current = null;
          }
          return;
        }

        stateRef.current = "picking";
        btn.classList.add("leaflet-control-pegman-button--active");
        container.classList.add("pegman-pick-cursor");

        showCoverageLayer();

        if (mapClickHandlerRef.current) {
          map.off("click", mapClickHandlerRef.current);
        }
        mapClickHandlerRef.current = onMobileMapClick;
        map.on("click", onMobileMapClick);
      }

      L.DomEvent.on(btn, "click", onBtnClick);

      btn._pegmanCleanup = function () {
        L.DomEvent.off(btn, "click", onBtnClick);
      };
    }

    function onKeyDown(e) {
      if (e.key !== "Escape") return;

      if (stateRef.current === "panorama") {
        closePanorama();
      } else if (stateRef.current === "dragging") {
        if (ghostRef.current) {
          ghostRef.current.remove();
          ghostRef.current = null;
        }
        map.dragging.enable();
        hideCoverageLayer();
        stateRef.current = "idle";
      } else if (stateRef.current === "picking") {
        stateRef.current = "idle";
        if (buttonElRef.current) {
          buttonElRef.current.classList.remove(
            "leaflet-control-pegman-button--active"
          );
        }
        container.classList.remove("pegman-pick-cursor");
        hideCoverageLayer();
        if (mapClickHandlerRef.current) {
          map.off("click", mapClickHandlerRef.current);
          mapClickHandlerRef.current = null;
        }
      }
    }

    document.addEventListener("keydown", onKeyDown);
    map.addControl(control);
    controlRef.current = control;

    return function () {
      const googleMaps = getGoogleMaps();
      document.removeEventListener("keydown", onKeyDown);

      if (controlRef.current) {
        map.removeControl(controlRef.current);
        controlRef.current = null;
      }

      buttonElRef.current?._pegmanCleanup?.();

      if (mapClickHandlerRef.current) {
        map.off("click", mapClickHandlerRef.current);
        mapClickHandlerRef.current = null;
      }

      if (panoramaRef.current) {
        try {
          googleMaps?.event?.clearInstanceListeners(panoramaRef.current);
        } catch (e) {
          console.warn("Panorama cleanup error:", e);
        }
        panoramaRef.current = null;
      }

      if (coverageLayerRef.current) {
        try {
          map.removeLayer(coverageLayerRef.current);
        } catch (e) {
          console.warn("Coverage layer cleanup error:", e);
        }
        coverageLayerRef.current = null;
      }

      removePegmanMarker();

      if (overlayRef.current?.parentNode) {
        overlayRef.current.remove();
        overlayRef.current = null;
      }

      if (ghostRef.current) {
        ghostRef.current.remove();
        ghostRef.current = null;
      }

      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }

      if (toastElRef.current?.parentNode) {
        toastElRef.current.remove();
        toastElRef.current = null;
      }

      container.classList.remove("pegman-pick-cursor");
      map.dragging.enable();
    };
  }, [
    closePanorama,
    ensureApi,
    hideCoverageLayer,
    hidePegmanControl,
    map,
    openPanorama,
    placePegmanMarker,
    removePegmanMarker,
    showCoverageLayer,
    showNoCoverageToast,
    getDragGhostHost,
  ]);

  return null;
};

PegmanControl.propTypes = {
  apiKey: PropTypes.string,
};

export default PegmanControl;
